import { Slot } from '@radix-ui/react-slot';
import { VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../lib/utils';
import { Spinner } from './Spinner';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-button text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary-darker focus-visible:outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        outlineNoRings: 'bg-primary text-primary-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        outlined:
          'border border-gray-300 bg-white text-gray-800 shadow-xs hover:bg-gray-50 dark:border-gray-600 dark:bg-white dark:text-gray-700 dark:hover:bg-gray-50',
      },
      size: {
        default: 'h-10 px-4 py-2',
        xs: 'h-6 px-2 py-1 text-xs',
        sm: 'h-9 rounded-button px-3 !text-base_brand',
        md: 'h-10 rounded-button px-4',
        lg: 'h-11 rounded-button px-8',
        icon: 'h-10 w-10',
        input: 'h-10 !w-full !max-w-full px-4',
        'input-sm': 'h-8 px-3 text-sm !text-base_brand',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loadingLayout?: 'over' | 'side';
  asChild?: boolean;
  loading?: boolean;
  loadingText?: string;
  classNames?: {
    spinner?: string;
  };
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      disabled,
      loading = false,
      loadingText,
      classNames,
      loadingLayout = 'side',
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(
          'relative',
          buttonVariants({ variant, size, className }),
          'relative',
        )}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <Spinner
            className={cn(classNames?.spinner, {
              '!m-0': loadingLayout === 'over',
            })}
            size='xs'
          />
        )}
        {!loading}
        {loading && loadingText ? (
          <span>{loadingText}</span>
        ) : loading && loadingLayout === 'over' ? null : (
          props.children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
